import { supabase } from '../../../services/supabase'
import type { BriefPortfolio } from './briefCatalog'
import type { EarlySignalsDbAnswer } from '../tryAnswerEarlySignalsFromDb'
import { bodyWithoutNextQuestion } from '../riskAnalystThread'

export type BriefRow = {
  id: string
  category: string
  portfolio: BriefPortfolio
}

export type BriefTurn = {
  id: string
  question: string
  routed_question: string
  answer: string
  created_at: string
  refreshed_at: string | null
}

export function answerText(answer: EarlySignalsDbAnswer | null): string {
  if (!answer) return 'That question is not in platform data for this brief.'
  if (answer.kind === 'narrative') {
    const name = answer.supplierName
    return name
      ? `A written next step for ${name} uses the live record. Ask “what is the highest risk in ${name}”.`
      : 'Name a supplier in this category to get a next step.'
  }
  if (answer.kind === 'table') {
    const preview = answer.table.rows
      .slice(0, 12)
      .map((row) => row.join(' · '))
      .join('\n')
    const lead = bodyWithoutNextQuestion(answer.content)
    return preview ? `${lead}\n\n${preview}` : lead
  }
  return bodyWithoutNextQuestion(answer.content)
}

/** Suppliers turned on for this company. A supplier with no row is not included. */
export async function enabledSupplierIds(companyId: string): Promise<Set<string>> {
  const ids = new Set<string>()
  const page = 1000
  let from = 0
  for (;;) {
    const { data, error } = await supabase
      .from('supplier_companies')
      .select('supplier_id')
      .eq('company_id', companyId)
      .range(from, from + page - 1)
    if (error) throw error
    const batch = data ?? []
    for (const row of batch) {
      if (row.supplier_id) ids.add(row.supplier_id)
    }
    if (batch.length < page) break
    from += page
  }
  return ids
}

export async function ensureBrief(
  userId: string,
  companyId: string,
  category: string,
  portfolio: BriefPortfolio
): Promise<BriefRow> {
  const { data, error } = await supabase
    .from('es_briefs')
    .upsert(
      {
        user_id: userId,
        company_id: companyId,
        category,
        portfolio,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,company_id,category,portfolio' }
    )
    .select('id, category, portfolio')
    .single()
  if (error) throw error
  return data as BriefRow
}

export async function listTurns(briefId: string): Promise<BriefTurn[]> {
  const { data, error } = await supabase
    .from('es_brief_turns')
    .select('id, question, routed_question, answer, created_at, refreshed_at')
    .eq('brief_id', briefId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as BriefTurn[]
}

export async function insertTurn(
  briefId: string,
  userId: string,
  question: string,
  routedQuestion: string,
  answer: string
): Promise<BriefTurn> {
  const { data, error } = await supabase
    .from('es_brief_turns')
    .insert({
      brief_id: briefId,
      user_id: userId,
      question,
      routed_question: routedQuestion,
      answer,
    })
    .select('id, question, routed_question, answer, created_at, refreshed_at')
    .single()
  if (error) throw error
  return data as BriefTurn
}

export async function replaceTurnAnswer(
  id: string,
  answer: string,
  routedQuestion?: string
): Promise<void> {
  const patch: { answer: string; refreshed_at: string; routed_question?: string } = {
    answer,
    refreshed_at: new Date().toISOString(),
  }
  if (routedQuestion) patch.routed_question = routedQuestion
  const { error } = await supabase.from('es_brief_turns').update(patch).eq('id', id)
  if (error) throw error
}
